import { Api, ApiError } from "../api";
import { empty, h, label, pill, replace, sectionHead } from "../dom";
import { money, qty, shortDate, when } from "../format";
import { navigate, reload } from "../nav";
import { awaitingSupplierMemo, CREDIT_FAULTS, CREDIT_REASONS, creditable, creditArgs, creditDone, decisionArgs, entryState, exactEa, kindWords, piles,
  supplierMemoArgs, unitFigure, unitHeading } from "../quickbooks";
import type { ActResult, Me, Page, QbEntry } from "../types";
import { plain } from "../words";
import { can, confirmButton, outcome, toggle } from "./shared";

// QuickBooks (qb_entries, invoices.read + costs.read; phase 5, P5-ap-ar.md): the entries the platform makes in
// QuickBooks: a supplier bill from a matched invoice at the BOL quantity, and on a Direct order the customer invoice it
// triggers. An Owner approves or rejects each proposed one (approve_qb_entry, quickbooks.approve; two presses, a
// rejection says why); approved ones post within about 15 minutes. One that didn't post shows why and can be approved
// again; one waiting for paperwork says what for. Nothing here pays, deletes or voids anything in QuickBooks.
// On a customer invoice that's in QuickBooks, someone with exceptions.write records a credit for a short, damaged or
// refused delivery once it's confirmed with both sides (record_credit; never automatic): it proposes the customer's
// credit memo and, the supplier at fault, the supplier's credit, which waits for their credit memo; that number is
// recorded on the waiting supplier credit when it arrives.

const SHOW = ["OPEN", "POSTED", "REJECTED", "ALL"];
const SHOW_WORDS: Record<string, string> = { OPEN: "Open", POSTED: "In QuickBooks", REJECTED: "Rejected", ALL: "All" };

function decisionPanel(api: Api, e: QbEntry): HTMLElement {
  const id = `qb${e.qbEntryId}`;
  const result = h("div", { class: "result", "aria-live": "polite" });
  const note = h("input", { id: `${id}-note`, type: "text", maxlength: 500, autocomplete: "off", placeholder: "Why, if you reject it" });
  const buttons: HTMLButtonElement[] = [];
  const decide = (decision: "APPROVE" | "REJECT") => async () => {
    const b = decisionArgs(e.qbEntryId, decision, note.value);
    if (!("args" in b)) return;
    buttons.forEach((x) => (x.disabled = true));
    try {
      const r = await api.tool<ActResult>("approve_qb_entry", b.args);
      if (r.status === "APPLIED") return reload();
      replace(result, outcome(r, decision === "APPROVE" ? "Approved." : "Rejected."));
    } catch (err) {
      replace(result, h("p", { class: "error" }, plain((err as ApiError).message)));
    }
    buttons.forEach((x) => (x.disabled = false));
  };
  const again = e.status === "FAILED";
  const approve = confirmButton(again ? "Approve again" : "Approve", "Press again to approve it", decide("APPROVE"), "button-primary");
  const reject = confirmButton("Reject", "Press again to reject it", decide("REJECT"), "button-quiet", () => {
    const b = decisionArgs(e.qbEntryId, "REJECT", note.value);
    if ("missing" in b) replace(result, h("p", { class: "error" }, b.missing));
    return "args" in b;
  });
  buttons.push(approve, reject);
  return h("div", { class: "stack-tight" },
    h("div", { class: "field" }, h("label", { for: note.id }, "Note"), note),
    h("div", { class: "button-row" }, approve, reject),
    h("p", { class: "caption" }, "Approved entries post to QuickBooks within about 15 minutes."),
    result);
}

type CreditAnswer = ActResult & { entries?: { kind: string; status: string }[] };

/** After a credit is recorded: what happened, and a button to show the new entries. */
async function recordCredit(api: Api, args: Record<string, unknown>, result: HTMLElement, button: HTMLButtonElement, supplierMemo: boolean) {
  button.disabled = true;
  try {
    const r = await api.tool<CreditAnswer>("record_credit", args);
    if (r.status === "APPLIED") {
      const show = h("button", { type: "button", class: "button button-small" }, "Show the new entries");
      show.addEventListener("click", () => reload());
      replace(result, h("p", { class: "ok" }, creditDone(r.entries, supplierMemo)), show);
      button.remove();
      return;
    }
    replace(result, outcome(r, "Recorded."));
  } catch (err) {
    replace(result, h("p", { class: "error" }, plain((err as ApiError).message)));
  }
  button.disabled = false;
}

/** Record a credit on a customer invoice in QuickBooks: item, quantity, what happened, whose fault, who confirmed it. */
function creditForm(api: Api, e: QbEntry): HTMLElement {
  const id = `cr${e.qbEntryId}`;
  const result = h("div", { class: "result", "aria-live": "polite" });
  const sku = h("select", { id: `${id}-sku` }, e.lines.map((l) => h("option", { value: l.sku }, `${l.sku} (${qty(l.qtyEa)} invoiced)`)));
  const n = h("input", { id: `${id}-qty`, type: "text", inputmode: "numeric", autocomplete: "off" });
  const reason = h("select", { id: `${id}-reason` }, Object.entries(CREDIT_REASONS).map(([v, t]) => h("option", { value: v }, t)));
  const fault = h("select", { id: `${id}-fault` }, Object.entries(CREDIT_FAULTS).map(([v, t]) => h("option", { value: v }, t)));
  const note = h("textarea", { id: `${id}-note`, rows: 2, maxlength: 1000, placeholder: "What was confirmed, with whom (e.g. POD short 120; Dan at Rusken and LTMW receiving agreed)" });
  const field = (el: HTMLElement, text: string) => h("div", { class: "field" }, h("label", { for: el.id }, text), el);
  const built = () => creditArgs(e, { sku: sku.value, qty: n.value, reason: reason.value, fault: fault.value, note: note.value });
  const record: HTMLButtonElement = confirmButton("Record the credit", "Press again to record it", () => {
    const b = built();
    if ("args" in b) void recordCredit(api, b.args, result, record, false);
  }, "button-primary", () => {
    const b = built();
    if ("missing" in b) replace(result, h("p", { class: "error" }, b.missing));
    else replace(result);
    return "args" in b;
  });
  return h("div", { class: "stack-tight" },
    h("p", { class: "caption" }, "Only once the short, damaged or refused delivery is confirmed with the customer and the supplier. " +
      "It proposes the customer's credit memo at the invoice's price; if the supplier is at fault, their credit at cost too, and the " +
      "customer's waits for it. Each is then approved here."),
    h("div", { class: "filters" }, field(sku, "Item"), field(n, "EA to credit"), field(reason, "What happened"), field(fault, "Whose fault")),
    field(note, "What was confirmed, with whom"),
    h("div", { class: "row-act" }, record), result);
}

/** On the supplier's waiting credit: their credit memo arrived; record its number, and the credit is then to approve. */
function supplierMemoForm(api: Api, e: QbEntry): HTMLElement {
  const id = `sm${e.qbEntryId}`;
  const result = h("div", { class: "result", "aria-live": "polite" });
  const ref = h("input", { id: `${id}-ref`, type: "text", maxlength: 30, autocomplete: "off", placeholder: "As written on it" });
  const note = h("input", { id: `${id}-note`, type: "text", maxlength: 1000, autocomplete: "off", placeholder: "Optional" });
  const record: HTMLButtonElement = confirmButton("Record it", "Press again to record it", () => {
    const b = supplierMemoArgs(e.creditMemoId!, ref.value, note.value);
    if ("args" in b) void recordCredit(api, b.args, result, record, true);
  }, "button-primary", () => {
    const b = supplierMemoArgs(e.creditMemoId!, ref.value, note.value);
    if ("missing" in b) replace(result, h("p", { class: "error" }, b.missing));
    return "args" in b;
  });
  return h("div", { class: "filters" },
    h("div", { class: "field" }, h("label", { for: ref.id }, "Their credit memo number"), ref),
    h("div", { class: "field" }, h("label", { for: note.id }, "Note"), note), record, result);
}

export function card(api: Api, me: Me, e: QbEntry): HTMLElement {
  const st = entryState(e.status);
  const canDecide = (e.status === "PROPOSED" || e.status === "FAILED") && can(me, "quickbooks.approve");
  const bill = e.kind === "BILL" || e.kind === "VENDOR_CREDIT";
  return h("section", { class: "panel decision", id: `qb-${e.qbEntryId}`, "aria-label": `${kindWords(e.kind)} ${e.docNumber ?? ""}` },
    h("div", { class: "row" },
      h("div", null,
        label(kindWords(e.kind)),
        h("h3", { class: "subheading" }, e.party, e.docNumber ? h("span", { class: "num" }, ` · ${e.docNumber}`) : null),
        h("p", { class: "caption" }, [e.txnDate ? `dated ${shortDate(e.txnDate)}` : null, e.customerPo ? `PO ${e.customerPo}` : null,
          e.memo, e.shipDate ? `shipped ${shortDate(e.shipDate)}` : null, e.trackingNum ? `BOL ${e.trackingNum}` : null].filter(Boolean).join(" · "))),
      h("div", null, h("span", { class: "number-md" }, money(e.amount)), " ", pill(st.text.toLowerCase(), st.tone))),
    e.status === "WAITING" && e.waitingFor ? h("p", { class: "notice" }, `Waiting for ${plain(e.waitingFor)}.`) : null,
    e.status === "FAILED" && e.lastError ? h("p", { class: "error" }, `Didn't post: ${plain(e.lastError)}`) : null,
    e.status === "REJECTED" && e.rejectedReason ? h("p", { class: "caption" }, `Rejected: ${e.rejectedReason}`) : null,
    e.dueDate || e.discountDate
      ? h("p", { class: "caption" }, [e.dueDate ? `Due ${shortDate(e.dueDate)}` : null,
          e.discountDate && e.discountAmount ? `${money(e.discountAmount)} off if paid by ${shortDate(e.discountDate)}` : null].filter(Boolean).join(" · "))
      : null,
    e.lines?.length
      ? h("div", { class: "scroll" }, h("table", { class: "tbl" },
          h("thead", null, h("tr", null, h("th", { scope: "col" }, "SKU"), h("th", { scope: "col", class: "r" }, "EA"),
            h("th", { scope: "col", class: "r" }, unitHeading(e.kind)), h("th", { scope: "col", class: "r" }, "Amount"))),
          h("tbody", null, e.lines.map((l) => h("tr", null,
            h("td", { class: "num" }, l.sku, l.description ? h("div", { class: "caption" }, l.description) : null),
            h("td", { class: "num r" }, qty(l.qtyEa, ""),
              l.invoicedQtyEa !== undefined && l.invoicedQtyEa !== l.qtyEa ? h("div", { class: "caption" }, `invoiced ${qty(l.invoicedQtyEa, "")}`) : null),
            h("td", { class: "num r" }, exactEa(unitFigure(e.kind, l)),
              l.sellPrice !== undefined && l.agreedCost !== undefined
                ? h("div", { class: "caption" }, `${exactEa(l.sellPrice)} − ${exactEa(l.agreedCost)}`) : null), h("td", { class: "num r" }, money(l.amount))))),
          bill && e.lines.some((l) => l.invoicedQtyEa !== undefined && l.invoicedQtyEa !== l.qtyEa)
            ? h("tfoot", null, h("tr", null, h("td", { colspan: 4, class: "caption" }, "Billed at the shipped (BOL) quantity, not the invoiced one.")))
            : null))
      : null,
    e.customerMemo ? h("p", { class: "caption" }, `On the invoice: ${e.customerMemo}`) : null,
    e.approvedBy ? h("p", { class: "caption" }, `Approved by ${e.approvedBy}${e.approvedAt ? `, ${when(e.approvedAt)}` : ""}`) : null,
    e.postedAt ? h("p", { class: "caption" }, `Posted ${when(e.postedAt)}${e.qboId ? ` (QuickBooks ${e.qboId})` : ""}`) : null,
    canDecide ? decisionPanel(api, e) : null,
    creditable(e) && can(me, "exceptions.write") ? toggle("Record a credit", () => creditForm(api, e)) : null,
    awaitingSupplierMemo(e) && can(me, "exceptions.write") ? toggle("Supplier's credit memo arrived", () => supplierMemoForm(api, e)) : null,
    (e.status === "PROPOSED" || e.status === "FAILED") && !can(me, "quickbooks.approve")
      ? h("p", { class: "caption" }, "Approving QuickBooks entries is an Owner's.") : null);
}

export async function quickbooks(api: Api, me: Me, query: URLSearchParams): Promise<HTMLElement> {
  const show = SHOW.includes(query.get("show") ?? "") ? query.get("show")! : "OPEN";
  const page = await api.tool<Page<QbEntry>>("qb_entries", { status: show, limit: 200 });
  const p = piles(page.rows);
  const showSelect = h("select", { id: "show" }, SHOW.map((s) => h("option", { value: s, selected: s === show }, SHOW_WORDS[s])));
  showSelect.addEventListener("change", () => navigate(`/quickbooks?${new URLSearchParams(showSelect.value === "OPEN" ? {} : { show: showSelect.value })}`));
  const section = (id: string, title: string, note: string, rows: QbEntry[]) => rows.length
    ? h("section", { class: "stack", "aria-labelledby": id }, sectionHead(id, title, note), rows.map((e) => card(api, me, e)))
    : null;

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "QuickBooks"),
      h("p", { class: "lede" }, page.rows.length
        ? [p.toApprove.length ? `${p.toApprove.length} to approve` : null, p.failed.length ? `${p.failed.length} didn't post` : null,
            p.waiting.length ? `${p.waiting.length} waiting for paperwork` : null, p.posting.length ? `${p.posting.length} posting` : null,
            p.done.length ? `${p.done.length} done` : null].filter(Boolean).join(", ") + "."
        : "No entries here."),
      h("p", { class: "caption" }, "Supplier bills at the shipped quantity times the PO's cost, and on Direct orders the customer invoice " +
        "each bill triggers. Nothing here pays, deletes or voids anything.")),
    h("form", { class: "filters" }, h("div", { class: "field" }, h("label", { for: "show" }, "Show"), showSelect)),
    section("approve", "To approve", "Check the lines and the amount", p.toApprove),
    section("failed", "Didn't post", "Fix the cause, then approve again", p.failed),
    section("posting", "Approved, posting", "Within about 15 minutes", p.posting),
    section("waiting", "Waiting for paperwork", "Nothing to do yet", p.waiting),
    section("done", show === "REJECTED" ? "Rejected" : "Done", "", p.done),
    page.rows.length ? null : empty("Nothing here", show === "OPEN" ? "No entries waiting, proposed or failed." : "Try Open or All."),
    page.next_cursor ? h("p", { class: "caption" }, "More than 200: narrow it with Show.") : null);
}
